import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAccount, useDisconnect } from 'wagmi';
import WalletConnect from '../../components/WalletConnect';
import PortfolioPreview from '../../components/discover/PortfolioPreview';
import CustomSwapWidget from '../../components/swap/CustomSwapWidget';
import { ZNSAddressDisplay } from '../../components/shared/ZNSAddressDisplay';

// ============================================
// WLFI BRAND COLORS
// Based on World Liberty Financial Brand Guidelines
// Updated: Black background with gold borders
// ============================================
export const WLFI_COLORS = {
  primary: '#1E3A8A', // Deep blue
  secondary: '#DC2626', // Red accent (patriotic)
  gold: '#D4AF37', // Gold/brass accent
  goldBorder: 'rgba(212, 175, 55, 0.4)', // Gold border (40% opacity)
  goldBorderHover: 'rgba(212, 175, 55, 0.6)', // Gold border hover (60% opacity)
  goldBorderLight: 'rgba(212, 175, 55, 0.2)', // Gold border subtle (20% opacity)
  background: '#000000', // Pure black background
  cardBackground: '#0a0a0a', // Slightly lighter black for cards
  navy: '#0A1628', // Legacy navy (kept for compatibility)
  white: '#FFFFFF',
  lightBlue: '#60A5FA',
  darkBlue: '#1E40AF',
  // Gradients
  headerGradient: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
  cardGradient: 'linear-gradient(180deg, rgba(20, 20, 20, 0.8) 0%, rgba(10, 10, 10, 0.9) 100%)',
  buttonGradient: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
  accentGradient: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
};

// ============ LAYOUT CONTAINER ============
const LayoutContainer = styled.div`
  min-height: 100vh;
  background: ${WLFI_COLORS.background};
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

// ============ NAVBAR ============
const NavBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: ${WLFI_COLORS.background};
  border-bottom: 1px solid ${WLFI_COLORS.goldBorder};
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const LogoText = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${WLFI_COLORS.white};
  letter-spacing: 0.5px;
  
  @media (max-width: 1024px) {
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const NavLink = styled.button<{ $active?: boolean }>`
  padding: 10px 16px;
  background: ${props => props.$active 
    ? 'rgba(212, 175, 55, 0.15)' 
    : 'transparent'};
  border: 1px solid ${props => props.$active 
    ? WLFI_COLORS.goldBorder
    : 'transparent'};
  border-radius: 8px;
  color: ${props => props.$active ? WLFI_COLORS.gold : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    color: ${WLFI_COLORS.gold};
    border-color: ${WLFI_COLORS.goldBorderLight};
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChainBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 20px;
  font-size: 13px;
  color: ${WLFI_COLORS.gold};
  
  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const ChainIcon = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  
  img {
    height: 16px;
    width: auto;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const WalletBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 10px;
  color: ${WLFI_COLORS.gold};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: ${WLFI_COLORS.goldBorderHover};
  }
`;

// ============ CONTENT WRAPPER ============
const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  min-height: calc(100vh - 64px - 56px);
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

// ============ MAIN CONTENT ============
const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  padding: 24px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// ============ RIGHT SIDEBAR ============
const RightSidebar = styled.aside`
  width: 380px;
  min-width: 380px;
  border-left: 1px solid ${WLFI_COLORS.goldBorder};
  background: ${WLFI_COLORS.background};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 64px - 56px);
  position: sticky;
  top: 64px;
  
  @media (max-width: 1200px) {
    width: 100%;
    min-width: 100%;
    border-left: none;
    border-top: 1px solid ${WLFI_COLORS.goldBorder};
    max-height: none;
    position: relative;
    top: 0;
    flex-direction: row;
    flex-wrap: wrap;
    
    & > * {
      flex: 1;
      min-width: 300px;
    }
  }
  
  @media (max-width: 700px) {
    padding: 16px;
    flex-direction: column;
    
    & > * {
      min-width: 100%;
    }
  }
`;

const SidebarSection = styled.div`
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 12px;
  padding: 16px;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${WLFI_COLORS.gold};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ============ FOOTER ============
const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  background: ${WLFI_COLORS.background};
  border-top: 1px solid ${WLFI_COLORS.goldBorder};
`;

const FooterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  
  a {
    color: ${WLFI_COLORS.gold};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
      color: ${WLFI_COLORS.gold};
    }
  }
`;

// ============ COMPONENT ============
const WLFILayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isConnected, address } = useAccount();
  const { disconnect } = useDisconnect();
  
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <LayoutContainer>
      <NavBar>
        <NavLeft>
          <LogoContainer onClick={() => navigate('/app/wlfi/explore')}>
            <Logo src="/wlfi_logomark.png" alt="World Liberty Financial" />
            <LogoText>World Liberty Financial: USD1 Launchpad</LogoText>
          </LogoContainer>
          
          <NavLinks>
            <NavLink 
              $active={isActive('/app/wlfi/explore')}
              onClick={() => navigate('/app/wlfi/explore')}
            >
              Explore
            </NavLink>
            <NavLink 
              $active={isActive('/app/wlfi/trade')}
              onClick={() => navigate('/app/wlfi/trade')}
            >
              Trade
            </NavLink>
            <NavLink 
              $active={isActive('/app/wlfi/portfolio')}
              onClick={() => navigate('/app/wlfi/portfolio')}
            >
              Portfolio
            </NavLink>
          </NavLinks>
        </NavLeft>
        
        <NavRight>
          <ChainBadge>
            <ChainIcon src="/landing-assets/ETH_logo.png" alt="Ethereum" />
            Ethereum
          </ChainBadge>
          
          {isConnected && address ? (
            <WalletBadge onClick={() => disconnect()} title="Click to disconnect">
              <ZNSAddressDisplay address={address} truncateLength={4} showIcon={false} />
            </WalletBadge>
          ) : (
            <WalletConnect useENS={true} />
          )}
          
          <PoweredBy>
            Powered by
            <img src="/mavrk_logo_white.png" alt="Mavrk" />
          </PoweredBy>
        </NavRight>
      </NavBar>
      
      <ContentWrapper>
        <MainContent>
          <Outlet />
        </MainContent>
        
        <RightSidebar>
          {/* Portfolio Section */}
          <SidebarSection>
            <SidebarTitle>
              📊 Portfolio
            </SidebarTitle>
            <PortfolioPreview />
          </SidebarSection>
          
          {/* Swap Widget */}
          <SidebarSection>
            <SidebarTitle>
              💱 Swap
            </SidebarTitle>
            <CustomSwapWidget />
          </SidebarSection>
        </RightSidebar>
      </ContentWrapper>
      
      <Footer>
        <FooterContent>
          <a href="https://worldlibertyfinancial.com" target="_blank" rel="noopener noreferrer">WLFI Website</a>
          <a href="https://twitter.com/worldlibertyfi" target="_blank" rel="noopener noreferrer">WLFI Twitter</a>
          <span>•</span>
          <span>Infrastructure by <a href="https://mavrk.xyz" target="_blank" rel="noopener noreferrer">Mavrk</a></span>
        </FooterContent>
      </Footer>
    </LayoutContainer>
  );
};

export default WLFILayout;
